import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, Users, ScrollText, LogOut, ShieldCheck, Search,
  CheckCircle2, XCircle, ArrowLeft, Layers, UserCog, Inbox, Clock3, Ban, RotateCcw, Trash2,
} from "lucide-react";
import { LogoMark } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { adminApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { useToast } from "../hooks/use-toast";

function StatCard({ icon: Icon, label, value, tone = "blue" }) {
  const tones = { blue: "bg-brand-light text-brand-blue", green: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600", slate: "bg-slate-100 text-slate-600" };
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${tones[tone]}`}><Icon className="w-5 h-5" /></div>
      <div className="font-display font-extrabold text-3xl text-brand-navy">{value}</div>
      <div className="text-sm text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function timeAgo(ts) {
  try {
    const diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return ""; }
}

export default function AdminConsole() {
  const { session, logout } = useAuth();
  const nav = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState("organizations");
  const [stats, setStats] = useState({});
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [leads, setLeads] = useState([]);
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(null); // { org, next }

  const load = async () => {
    try {
      const [s, o] = await Promise.all([adminApi.stats(), adminApi.orgs()]);
      setStats(s); setOrgs(o);
    } catch (e) { /* guarded */ }
  };
  const loadLeads = () => adminApi.leads().then(setLeads).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === "users" && users.length === 0) adminApi.users().then(setUsers).catch(() => {});
    if (tab === "audit" && audit.length === 0) adminApi.audit().then(setAudit).catch(() => {});
    if (tab === "leads" && leads.length === 0) loadLeads();
  }, [tab]);

  const doLogout = () => { logout(); nav("/"); };

  const applyStatus = async () => {
    const { org, next } = confirm;
    try {
      await adminApi.setStatus(org.id, next);
      toast({ title: `Organisation ${next}`, description: `${org.name} is now ${next}.` });
      await load();
    } catch (e) {
      toast({ title: "Action failed", description: e?.response?.data?.detail || "Try again", variant: "destructive" });
    }
    setConfirm(null);
  };

  const openDetail = async (org) => {
    try { setDetail(await adminApi.orgDetail(org.id)); } catch (e) {}
  };

  const approveOrg = async (org) => {
    try { await adminApi.approve(org.id); toast({ title: "Organisation approved", description: `${org.name} now has a 15-day demo.` }); await load(); }
    catch (e) { toast({ title: "Action failed", description: e?.response?.data?.detail || "Try again", variant: "destructive" }); }
  };
  const extendOrg = async (org) => {
    try { const r = await adminApi.extend(org.id, 15); toast({ title: "Demo extended", description: `${org.name} now has ${r.daysRemaining} days remaining.` }); await load(); }
    catch (e) { toast({ title: "Action failed", description: e?.response?.data?.detail || "Try again", variant: "destructive" }); }
  };
  const updateLead = async (lead, statusVal) => {
    try { await adminApi.leadStatus(lead.id, statusVal); setLeads((ls) => ls.map((l) => l.id === lead.id ? { ...l, status: statusVal } : l)); }
    catch (e) { toast({ title: "Could not update lead", variant: "destructive" }); }
  };

  const tabs = [
    { key: "organizations", label: "Organisations", icon: Building2 },
    { key: "leads", label: "Leads", icon: Inbox, badge: stats.newLeads },
    { key: "users", label: "Users", icon: Users },
    { key: "audit", label: "Audit Log", icon: ScrollText },
  ];

  const filteredOrgs = orgs.filter((o) => !query || o.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Top bar */}
      <header className="border-b border-white/10 bg-slate-900/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size={34} />
            <div>
              <div className="font-display font-extrabold text-lg leading-none"><span className="text-white">HR</span><span className="text-brand-sky">Flo</span></div>
              <div className="text-[10px] tracking-[0.2em] text-brand-sky font-semibold flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> SUPER ADMIN</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-300 hidden sm:block">{session?.user?.name}</span>
            <button onClick={doLogout} className="flex items-center gap-1.5 text-sm font-medium text-slate-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg px-3 h-10 transition-colors"><LogOut className="w-4 h-4" /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-display font-extrabold text-3xl">Platform Control</h1>
          <p className="text-slate-400 mt-1">Manage every organisation, user and activity across HRFlo.</p>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-8">
          <StatCardDark icon={Layers} label="Organisations" value={stats.organizations ?? 0} />
          <StatCardDark icon={Clock3} label="Pending" value={stats.pendingOrganizations ?? 0} tone="amber" />
          <StatCardDark icon={CheckCircle2} label="Active" value={stats.activeOrganizations ?? 0} tone="green" />
          <StatCardDark icon={XCircle} label="Suspended" value={stats.suspendedOrganizations ?? 0} tone="red" />
          <StatCardDark icon={UserCog} label="Users" value={stats.users ?? 0} tone="sky" />
          <StatCardDark icon={Inbox} label="Leads" value={stats.leads ?? 0} tone="blue" />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-5 bg-white/5 p-1 rounded-xl w-fit">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? "bg-brand-blue text-white" : "text-slate-300 hover:text-white"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
              {t.badge ? <span className="ml-1 text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5 py-0.5 leading-none">{t.badge}</span> : null}
            </button>
          ))}
        </div>

        {/* Organisations */}
        {tab === "organizations" && (
          <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center gap-2">
              <Search className="w-4 h-4 text-slate-400" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search organisations..." className="bg-transparent outline-none text-sm w-full text-white placeholder:text-slate-500" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400 border-b border-white/10">
                  <th className="py-3 px-5 font-medium">Organisation</th>
                  <th className="py-3 px-3 font-medium">Plan</th>
                  <th className="py-3 px-3 font-medium">Users</th>
                  <th className="py-3 px-3 font-medium">Trial</th>
                  <th className="py-3 px-3 font-medium">Status</th>
                  <th className="py-3 px-5 font-medium text-right">Actions</th>
                </tr></thead>
                <tbody>
                  {filteredOrgs.map((o) => (
                    <tr key={o.id} className="border-b border-white/5 last:border-0 hover:bg-white/5">
                      <td className="py-3 px-5">
                        <div className="font-semibold text-white">{o.name}</div>
                        <div className="text-xs text-slate-400">{o.country} · {o.employeeCount} employees · {new Date(o.created_at).toLocaleDateString()}</div>
                      </td>
                      <td className="py-3 px-3 text-slate-300">{o.plan}</td>
                      <td className="py-3 px-3 text-slate-300">{o.userCount}</td>
                      <td className="py-3 px-3">
                        {o.account_type === "demo" && o.status === "active" && o.daysRemaining != null ? (
                          <span className={`text-xs font-semibold ${o.daysRemaining <= 3 ? "text-amber-400" : "text-slate-300"}`}>{o.daysRemaining}d left</span>
                        ) : o.account_type === "internal" ? (
                          <span className="text-xs text-slate-500">—</span>
                        ) : (
                          <span className="text-xs text-slate-500">{o.status === "pending" ? "not started" : "—"}</span>
                        )}
                      </td>
                      <td className="py-3 px-3"><StatusBadge status={o.status} /></td>
                      <td className="py-3 px-5">
                        <div className="flex items-center justify-end gap-2.5">
                          <button onClick={() => openDetail(o)} className="text-xs font-medium text-brand-sky hover:text-white transition-colors">View</button>
                          {o.status === "pending" && (
                            <button onClick={() => approveOrg(o)} className="text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Approve</button>
                          )}
                          {o.status === "active" && o.account_type === "demo" && (
                            <button onClick={() => extendOrg(o)} className="text-xs font-medium text-brand-sky hover:text-white transition-colors flex items-center gap-1"><Clock3 className="w-3.5 h-3.5" />Extend</button>
                          )}
                          {o.status === "active" && (
                            <button onClick={() => setConfirm({ org: o, next: "suspended" })} className="text-xs font-medium text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1"><Ban className="w-3.5 h-3.5" />Suspend</button>
                          )}
                          {(o.status === "suspended" || o.status === "terminated") && (
                            <button onClick={() => setConfirm({ org: o, next: "active" })} className="text-xs font-medium text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1"><RotateCcw className="w-3.5 h-3.5" />Reactivate</button>
                          )}
                          {o.status !== "terminated" && (
                            <button onClick={() => setConfirm({ org: o, next: "terminated" })} className="text-xs font-medium text-red-400 hover:text-red-300 transition-colors flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />Terminate</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredOrgs.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No organisations found.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Leads */}
        {tab === "leads" && (
          <div className="bg-white/5 rounded-2xl border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-white/10">
                <th className="py-3 px-5 font-medium">Lead</th>
                <th className="py-3 px-3 font-medium">Company</th>
                <th className="py-3 px-3 font-medium hidden lg:table-cell">Message</th>
                <th className="py-3 px-3 font-medium">Received</th>
                <th className="py-3 px-5 font-medium">Status</th>
              </tr></thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-white/5 last:border-0 hover:bg-white/5 align-top">
                    <td className="py-3 px-5"><div className="font-semibold text-white">{l.name}</div><div className="text-xs text-slate-400">{l.email}</div></td>
                    <td className="py-3 px-3 text-slate-300">{l.company}</td>
                    <td className="py-3 px-3 text-slate-400 hidden lg:table-cell max-w-xs"><div className="line-clamp-2">{l.message || "—"}</div></td>
                    <td className="py-3 px-3 text-slate-400 whitespace-nowrap">{timeAgo(l.created_at)}</td>
                    <td className="py-3 px-5">
                      <select value={l.status} onChange={(e) => updateLead(l, e.target.value)} className="bg-slate-800 border border-white/10 rounded-lg text-xs text-white px-2 h-8 outline-none focus:border-brand-blue">
                        {["new", "contacted", "qualified", "converted", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {leads.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-slate-400">No leads yet. Book a Demo submissions will appear here.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Users */}
        {tab === "users" && (
          <div className="bg-white/5 rounded-2xl border border-white/10 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-white/10">
                <th className="py-3 px-5 font-medium">Name</th>
                <th className="py-3 px-3 font-medium">Email</th>
                <th className="py-3 px-3 font-medium">Role</th>
                <th className="py-3 px-5 font-medium">Organisation</th>
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-white/5 last:border-0 hover:bg-white/5">
                    <td className="py-3 px-5 font-semibold text-white">{u.name}</td>
                    <td className="py-3 px-3 text-slate-300">{u.email}</td>
                    <td className="py-3 px-3"><Badge className="bg-brand-blue/20 text-brand-sky hover:bg-brand-blue/20">{u.role}</Badge></td>
                    <td className="py-3 px-5 text-slate-300">{u.organization_name}</td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-slate-400">No users yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Audit */}
        {tab === "audit" && (
          <div className="bg-white/5 rounded-2xl border border-white/10 p-2 divide-y divide-white/5">
            {audit.length === 0 && <div className="py-10 text-center text-slate-400">No activity recorded.</div>}
            {audit.map((a) => (
              <div key={a.id} className="flex items-center gap-3 px-4 py-3">
                <Badge className={`${a.action === "delete" ? "bg-red-500/15 text-red-400" : a.action === "create" ? "bg-emerald-500/15 text-emerald-400" : "bg-brand-blue/20 text-brand-sky"} hover:bg-current/10 capitalize`}>{a.action}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{a.summary}</div>
                  <div className="text-xs text-slate-400">{a.organization_name} · {a.actor_name}</div>
                </div>
                <div className="text-xs text-slate-500 whitespace-nowrap">{timeAgo(a.timestamp)}</div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Org detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDetail(null)} />
          <div className="relative bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg p-6">
            <button onClick={() => setDetail(null)} className="text-slate-400 hover:text-white text-sm flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
            <h3 className="font-display font-bold text-xl text-white">{detail.organization.name}</h3>
            <p className="text-slate-400 text-sm">{detail.organization.country} · {detail.organization.plan} · {detail.employeeCount} employees</p>
            <div className="mt-5">
              <div className="text-sm font-semibold text-slate-300 mb-2">Users</div>
              <div className="space-y-2">
                {detail.users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                    <div><div className="text-sm text-white">{u.name}</div><div className="text-xs text-slate-400">{u.email}</div></div>
                    <Badge className="bg-brand-blue/20 text-brand-sky hover:bg-brand-blue/20">{u.role}</Badge>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.next === "suspended" ? "Suspend" : confirm?.next === "terminated" ? "Terminate" : "Reactivate"} organisation?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.next === "suspended"
                ? `${confirm?.org?.name} users will be blocked from signing in until reactivated.`
                : confirm?.next === "terminated"
                ? `${confirm?.org?.name} will be permanently terminated and all its users blocked from signing in.`
                : `${confirm?.org?.name} users will regain access immediately.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={applyStatus} className={confirm?.next === "active" ? "bg-brand-blue hover:bg-brand-bluedark" : "bg-destructive hover:bg-destructive/90"}>
              {confirm?.next === "suspended" ? "Suspend" : confirm?.next === "terminated" ? "Terminate" : "Reactivate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-500/15 text-emerald-400",
    pending: "bg-amber-500/15 text-amber-400",
    suspended: "bg-orange-500/15 text-orange-400",
    terminated: "bg-red-500/15 text-red-400",
  };
  return <Badge className={`${map[status] || "bg-slate-500/15 text-slate-300"} hover:bg-current/10 capitalize`}>{status}</Badge>;
}

function StatCardDark({ icon: Icon, label, value, tone = "blue" }) {
  const tones = { blue: "bg-brand-blue/20 text-brand-sky", green: "bg-emerald-500/15 text-emerald-400", red: "bg-red-500/15 text-red-400", sky: "bg-sky-500/15 text-sky-400", amber: "bg-amber-500/15 text-amber-400" };
  return (
    <div className="bg-white/5 rounded-2xl border border-white/10 p-5">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${tones[tone]}`}><Icon className="w-5 h-5" /></div>
      <div className="font-display font-extrabold text-3xl text-white">{value}</div>
      <div className="text-sm text-slate-400 mt-0.5">{label}</div>
    </div>
  );
}
